/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { FileCheck, Download, Calendar, Filter, TrendingUp, DollarSign, Users } from 'lucide-react';
import { useVendorReports } from '../../services/vendorAnalyticsService';
import CustomSelect from '../../components/shared/CustomSelect';

const VendorReports = () => {
  const [reportType, setReportType] = useState('bookings');
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorReports(reportType, period);

  const reports = data?.data || {};

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Reports</h1>
          <p className="text-sm text-gray-600">Generate and download detailed reports</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4 sm:mt-0">
          <div className="w-full sm:w-48">
            <CustomSelect
              value={reportType}
              onChange={(value) => setReportType(value)}
              options={[
                { value: 'bookings', label: 'Bookings Report' },
                { value: 'revenue', label: 'Revenue Report' },
                { value: 'clients', label: 'Clients Report' },
                { value: 'services', label: 'Services Report' }
              ]}
              size="sm"
              className="w-full"
            />
          </div>
          <div className="w-full sm:w-48">
            <CustomSelect
              value={period}
              onChange={(value) => setPeriod(value)}
              options={[
                { value: 'week', label: 'This Week' },
                { value: 'month', label: 'This Month' },
                { value: 'quarter', label: 'This Quarter' },
                { value: 'year', label: 'This Year' }
              ]}
              size="sm"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Failed to load reports. Please try again.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report - {period.charAt(0).toUpperCase() + period.slice(1)}
              </h2>
              <button className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm mt-4 sm:mt-0 w-full sm:w-auto justify-center">
                <Download className="w-4 h-4" />
                <span>Download Report</span>
              </button>
            </div>

            {reportType === 'bookings' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.total_bookings || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{reports.completed_bookings || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">{reports.cancelled_bookings || 0}</p>
                </div>
              </div>
            )}

            {reportType === 'revenue' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">${reports.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Average Booking</p>
                  <p className="text-2xl font-bold text-gray-900">${reports.average_booking?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Net Earnings</p>
                  <p className="text-2xl font-bold text-green-600">${reports.net_earnings?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            )}

            {reportType === 'clients' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.total_clients || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">New Clients</p>
                  <p className="text-2xl font-bold text-blue-600">{reports.new_clients || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Repeat Clients</p>
                  <p className="text-2xl font-bold text-green-600">{reports.repeat_clients || 0}</p>
                </div>
              </div>
            )}

            {reportType === 'services' && (
              <div className="space-y-4">
                {reports.top_services && reports.top_services.length > 0 ? (
                  reports.top_services.map((service, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        <p className="text-sm text-gray-500">{service.bookings_count || 0} bookings</p>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">
                        ${service.revenue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">No service data available</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default VendorReports;

